import React, { useState } from 'react';

const SearchFilters = ({ filters, onChange, searchType }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  // Medical Specialties organized by categories
  const doctorSpecialties = {
    'Primary Care': [
      'Family Medicine',
      'Internal Medicine', 
      'General Practice',
      'Pediatrics',
      'Geriatrics'
    ],
    'Heart & Vascular': [
      'Cardiology',
      'Cardiac Surgery',
      'Vascular Surgery',
      'Interventional Cardiology'
    ],
    'Brain & Nervous System': [
      'Neurology',
      'Neurosurgery',
      'Psychiatry',
      'Psychology',
      'Sleep Medicine'
    ],
    'Women\'s Health': [
      'Obstetrics & Gynecology',
      'Reproductive Endocrinology',
      'Maternal-Fetal Medicine',
      'Gynecologic Oncology'
    ],
    'Surgical Specialties': [
      'General Surgery',
      'Orthopedic Surgery',
      'Plastic Surgery',
      'Urological Surgery',
      'ENT Surgery'
    ],
    'Diagnostic & Imaging': [
      'Radiology',
      'Pathology',
      'Nuclear Medicine',
      'Interventional Radiology'
    ],
    'Other Specialties': [
      'Dermatology',
      'Ophthalmology',
      'Oncology',
      'Endocrinology',
      'Gastroenterology',
      'Pulmonology',
      'Rheumatology',
      'Infectious Disease',
      'Emergency Medicine',
      'Anesthesiology'
    ]
  };

  // Healthcare Provider Services organized by facility type
  const providerServices = {
    'Hospital Services': [
      'Emergency Care',
      'Inpatient Surgery',
      'Intensive Care (ICU)',
      'Maternity & Labor',
      'Trauma Center',
      'Cardiac Catheterization',
      'Dialysis',
      'Rehabilitation',
      'Blood Bank',
      'Pharmacy Services'
    ],
    'Urgent & Walk-in Care': [
      'Urgent Care',
      'Walk-in Clinic',
      'Same-day Appointments',
      'Minor Emergency',
      'Sports Injuries',
      'Occupational Health',
      'Travel Medicine',
      'Immunizations'
    ],
    'Diagnostic Services': [
      'MRI Scans',
      'CT Scans',
      'X-Ray',
      'Ultrasound',
      'Mammography',
      'Blood Tests',
      'Urine Tests',
      'Biopsy',
      'Colonoscopy',
      'Endoscopy',
      'EKG/ECG',
      'Stress Testing'
    ],
    'Dental Services': [
      'Teeth Cleaning',
      'Teeth Whitening',
      'Dental Implants',
      'Root Canal',
      'Oral Surgery',
      'Orthodontics (Braces)',
      'Invisalign',
      'Dental Crowns',
      'Dental Bridges',
      'Dentures',
      'Periodontal Treatment',
      'Cosmetic Dentistry'
    ],
    'Beauty & Aesthetic Services': [
      'Botox Injections',
      'Dermal Fillers',
      'Microneedling',
      'Chemical Peels',
      'Laser Hair Removal',
      'Laser Skin Resurfacing',
      'CoolSculpting',
      'Lip Enhancement',
      'Anti-aging Treatments',
      'Acne Treatment',
      'Scar Removal',
      'Skin Tightening'
    ],
    'Physical Therapy & Rehab': [
      'Physical Therapy',
      'Occupational Therapy',
      'Speech Therapy',
      'Sports Rehabilitation',
      'Post-surgical Recovery',
      'Pain Management',
      'Massage Therapy',
      'Chiropractic Care',
      'Acupuncture'
    ],
    'Mental Health Services': [
      'Individual Therapy',
      'Group Therapy',
      'Family Counseling',
      'Couples Therapy',
      'Depression Treatment',
      'Anxiety Treatment',
      'PTSD Treatment',
      'Addiction Counseling',
      'Child Psychology',
      'Psychiatric Evaluation'
    ],
    'Specialized Consultations': [
      'Second Opinion Consultation',
      'Pre-surgical Consultation',
      'Telemedicine Consultation',
      'Nutritional Counseling',
      'Genetic Counseling',
      'Fertility Consultation',
      'Pain Consultation',
      'Cancer Consultation'
    ]
  };

  // Location suggestions
  const locationSuggestions = [
    'Near Me',
    'Downtown',
    'Uptown', 
    'Medical District',
    'Suburbs',
    'Within 5 miles',
    'Within 10 miles',
    'Within 25 miles'
  ];

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    onChange(newFilters);
  };

  const handleServiceToggle = (service) => {
    const currentServices = filters.services || [];
    const newServices = currentServices.includes(service)
      ? currentServices.filter(s => s !== service)
      : [...currentServices, service];
    
    handleFilterChange('services', newServices);
  };

  const handleSpecialtySelect = (specialty) => {
    handleFilterChange('specialty', specialty === filters.specialty ? '' : specialty);
  };

  const clearAllFilters = () => {
    onChange({
      location: '',
      specialty: '',
      rating: 0,
      availability: '',
      services: []
    });
  };

  const hasActiveFilters = Object.values(filters).some(value => 
    Array.isArray(value) ? value.length > 0 : value
  );

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 lg:sticky lg:top-4">
      {/* Filter Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <span className="mr-2">🎯</span>
            Smart Filters
          </h3>
          <div className="flex items-center space-x-2">
            {hasActiveFilters && (
              <button
                onClick={clearAllFilters}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Clear All
              </button>
            )}
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg
                className={`w-5 h-5 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Filter Content */}
      {isExpanded && (
        <div className="px-6 py-4 space-y-6 max-h-96 overflow-y-auto">
          {/* Location Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              📍 Location
            </label>
            <div className="space-y-2">
              <input
                type="text"
                value={filters.location}
                onChange={(e) => handleFilterChange('location', e.target.value)}
                placeholder="Enter city, zip code, or address"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
              
              {/* Quick location buttons */}
              <div className="flex flex-wrap gap-1 mt-2">
                {locationSuggestions.map(location => (
                  <button
                    key={location}
                    onClick={() => handleFilterChange('location', location)}
                    className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full hover:bg-blue-100 hover:text-blue-700 transition-colors"
                  >
                    {location}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Doctor Specialties (for doctors and all) */}
          {(searchType === 'doctors' || searchType === 'all') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                👨‍⚕️ Medical Specialties
              </label>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {Object.entries(doctorSpecialties).map(([category, specialties]) => (
                  <div key={category}>
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      {category}
                    </h4>
                    <div className="grid grid-cols-1 gap-1 ml-2">
                      {specialties.map(specialty => (
                        <button
                          key={specialty}
                          onClick={() => handleSpecialtySelect(specialty)}
                          className={`text-left px-3 py-2 text-sm rounded-lg transition-colors ${
                            filters.specialty === specialty
                              ? 'bg-blue-100 text-blue-700 font-medium'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {specialty}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Healthcare Provider Services (for providers and all) */}
          {(searchType === 'healthcare-providers' || searchType === 'all') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                🏥 Healthcare Services
              </label>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {Object.entries(providerServices).map(([category, services]) => (
                  <div key={category}>
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      {category}
                    </h4>
                    <div className="grid grid-cols-1 gap-1 ml-2">
                      {services.map(service => (
                        <label key={typeof service === 'string' ? service : service.name || service.id} className="flex items-center cursor-pointer hover:bg-gray-50 px-2 py-1 rounded">
                          <input
                            type="checkbox"
                            checked={(filters.services || []).includes(typeof service === 'string' ? service : service.name)}
                            onChange={() => handleServiceToggle(typeof service === 'string' ? service : service.name)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-3 w-3"
                          />
                          <span className="ml-2 text-sm text-gray-700">
                            {typeof service === 'string' ? service : service.name || service}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Rating Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              ⭐ Minimum Rating
            </label>
            <div className="space-y-2">
              {[
                { value: 0, label: 'Any Rating', stars: '' },
                { value: 3, label: '3+ Stars', stars: '⭐⭐⭐' },
                { value: 4, label: '4+ Stars', stars: '⭐⭐⭐⭐' },
                { value: 4.5, label: '4.5+ Stars', stars: '⭐⭐⭐⭐⭐' },
                { value: 5, label: '5 Stars Only', stars: '⭐⭐⭐⭐⭐' }
              ].map(option => (
                <button
                  key={option.value}
                  onClick={() => handleFilterChange('rating', option.value)}
                  className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                    filters.rating === option.value
                      ? 'bg-yellow-100 text-yellow-800 font-medium'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span className="text-sm">{option.stars} {option.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Availability Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              📅 Availability
            </label>
            <div className="space-y-2">
              {[
                { value: '', label: 'Any Time', icon: '🕐' },
                { value: 'today', label: 'Available Today', icon: '⚡' },
                { value: 'tomorrow', label: 'Available Tomorrow', icon: '📅' },
                { value: 'this-week', label: 'This Week', icon: '📆' },
                { value: 'next-week', label: 'Next Week', icon: '🗓️' },
                { value: 'emergency', label: '24/7 Emergency', icon: '🚨' }
              ].map(option => (
                <button
                  key={option.value}
                  onClick={() => handleFilterChange('availability', option.value)}
                  className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                    filters.availability === option.value
                      ? 'bg-green-100 text-green-700 font-medium'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span className="text-sm">
                    {option.icon} {option.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Quick Filter Presets */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              ⚡ Quick Filters
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => {
                  handleFilterChange('availability', 'today');
                  handleFilterChange('rating', 4);
                }}
                className="px-3 py-2 text-xs bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors font-medium"
              >
                🔥 Available & Top Rated
              </button>
              <button
                onClick={() => {
                  handleFilterChange('location', 'Near Me');
                  handleFilterChange('availability', 'emergency');
                }}
                className="px-3 py-2 text-xs bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors font-medium"
              >
                🚨 Emergency Near Me
              </button>
              <button
                onClick={() => {
                  handleFilterChange('specialty', 'Family Medicine');
                  handleFilterChange('availability', 'this-week');
                }}
                className="px-3 py-2 text-xs bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors font-medium"
              >
                👨‍⚕️ Family Doctor
              </button>
              <button
                onClick={() => {
                  handleServiceToggle('Urgent Care');
                  handleFilterChange('availability', 'today');
                }}
                className="px-3 py-2 text-xs bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors font-medium"
              >
                ⚡ Urgent Care Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchFilters;